import React, { useCallback, useEffect, useState } from 'react';
import { intervalToDuration } from 'date-fns';
import { useClient } from 'urql';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { NucleoCirclePause, NucleoCirclePlay } from '@lyearn/icons';
import { Popover, Stack, Tooltip, Typography, useSnackbar } from '@lyearn/ui';
import fetchQuery, { FetchPolicy } from '@/helper/data-op/fetchQuery';
import { useFeatureFlags } from '@/helper/FeatureFlags';
import { FeatureFlags } from '@/helper/FeatureFlags';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import {
  ActionItemsDocument,
  ActionItemsQuery,
} from '@/modules/tasks/features/actionItems/graphql/queries/generated/actionItems';
import { ActionItemType, TimeLogEntityTypeEnum, ToggleTimerEnum } from '@/types/schema';

import TimerMenu from '../../TaskDashboard/TimerMenu';
import { useToggleTimerMutation } from '../../TaskDashboard/TimerMenu/graphql/mutations/generated/toggleTimer';
import { TableRowData } from '../types';

function formatTime(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (n: number) => String(n).padStart(2, '0');

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

function formatDuration(totalSeconds: number): string {
  const duration = intervalToDuration({ start: 0, end: totalSeconds * 1000 });

  const parts = [];
  const totalDays =
    (duration.years ?? 0) * 365 + (duration.months ?? 0) * 30 + (duration.days ?? 0);

  if (totalDays > 0) {
    parts.push(`${totalDays}d`);
  }
  if (duration.hours && duration.hours > 0) {
    parts.push(`${duration.hours}h`);
  }
  if (duration.minutes && duration.minutes > 0) {
    parts.push(`${duration.minutes}m`);
  }

  if (parts.length === 0) {
    if (duration.seconds && duration.seconds > 0) {
      return `${duration.seconds}s`;
    }
    return '0m';
  }

  return parts.join(' ');
}

interface TimeTrackedContentProps {
  data: Pick<ActionItemType, 'id' | 'fileId' | 'totalTimeSpent'>;
  disableInteractiveTooltip?: boolean;
  className?: string;
}

export function TimeTrackedContent({
  data,
  disableInteractiveTooltip = false,
  className,
}: TimeTrackedContentProps) {
  const { t } = useTranslation();
  const [showPopover, { on, off, toggle: togglePopover }] = useToggle(false);
  const [seconds, setSeconds] = useState(0);
  const { showErrorSnackbar } = useSnackbar();
  const [, toggleTimer] = useToggleTimerMutation();
  const user = useUserContext();

  const ongoingTimer = user?.ongoingTimer;

  const client = useClient();

  const isTimerRunningForThisTask =
    !!ongoingTimer?.startTime && !ongoingTimer.endTime && ongoingTimer.entityId === data.id;

  const isAnyTimerRunning = !!ongoingTimer?.startTime && !ongoingTimer.endTime;

  const refetchActionItem = useCallback(async () => {
    await fetchQuery<ActionItemsQuery>(
      client,
      ActionItemsDocument,
      {
        filters: {
          actionItemIds: [ongoingTimer?.entityId ?? ''],
        },
      },
      {
        requestPolicy: FetchPolicy.NETWORK_ONLY,
      },
    );
  }, [client, ongoingTimer?.entityId]);

  useEffect(() => {
    if (isTimerRunningForThisTask && ongoingTimer?.startTime) {
      const startTime = new Date(ongoingTimer.startTime).getTime();
      const interval = setInterval(() => {
        setSeconds(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);

      setSeconds(Math.floor((Date.now() - startTime) / 1000));

      return () => clearInterval(interval);
    } else {
      setSeconds(0);
    }
  }, [isTimerRunningForThisTask, ongoingTimer]);

  const handleStartTimer = (e: React.MouseEvent) => {
    e.stopPropagation();

    const startNewTimer = () => {
      toggleTimer({
        data: {
          entityId: data.id,
          userId: user.id,
          entityType: TimeLogEntityTypeEnum.Task,
          parentEntityId: data.fileId ?? '',
          parentEntityType: TimeLogEntityTypeEnum.File,
          toggle: ToggleTimerEnum.Start,
        },
      }).then((res) => {
        if (res?.data?.toggleTimer?.success) {
          user.refetchUser();
          // refetchActionItem();
        } else {
          showErrorSnackbar({ message: t('Common:Error.SomethingWentWrong') });
        }
      });
    };

    if (isAnyTimerRunning && ongoingTimer?.entityId && ongoingTimer?.entityType) {
      toggleTimer({
        data: {
          entityId: ongoingTimer.entityId,
          entityType: ongoingTimer.entityType,
          parentEntityId: ongoingTimer.parentEntityId,
          parentEntityType: ongoingTimer.parentEntityType,
          userId: user.id,
          toggle: ToggleTimerEnum.End,
        },
      }).then((stopResult) => {
        if (stopResult?.data?.toggleTimer?.success) {
          startNewTimer();
          // refetchActionItem();
        } else {
          showErrorSnackbar({ message: t('Common:Error.SomethingWentWrong') });
        }
      });
    } else {
      startNewTimer();
    }
  };

  const handleStopTimer = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isTimerRunningForThisTask || !ongoingTimer?.id) return;
    toggleTimer({
      data: {
        entityId: data.id,
        userId: user.id,
        entityType: TimeLogEntityTypeEnum.Task,
        parentEntityId: data.fileId ?? '',
        parentEntityType: TimeLogEntityTypeEnum.File,
        toggle: ToggleTimerEnum.End,
      },
    }).then((res) => {
      if (res?.data?.toggleTimer?.success) {
        user.refetchUser();
        // refetchActionItem();
      } else {
        showErrorSnackbar({ message: t('Common:Error.SomethingWentWrong') });
      }
    });
  };

  const handleIconClick = isTimerRunningForThisTask ? handleStopTimer : handleStartTimer;

  const handleContainerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isTimerRunningForThisTask) {
      handleStopTimer(e);
    } else {
      if (!showPopover) {
        on();
      }
    }
  };

  const totalTimeSpent = data.totalTimeSpent ?? 0;

  return (
    <Stack
      alignItems="center"
      className={
        className
          ? className
          : 'border border-transparent rounded-8 hover:border-border active:border-primary p-8 w-full cursor-pointer'
      }
      direction="row"
      gap={8}
      role="button"
      onClick={handleContainerClick}>
      <Tooltip
        disableInteractive={disableInteractiveTooltip}
        title={isTimerRunningForThisTask ? 'Stop Timer' : 'Start Timer'}>
        <Stack className="flex-shrink-0" onClick={handleIconClick}>
          {isTimerRunningForThisTask ? (
            <NucleoCirclePause fillColorToken="icon-error" height={16} width={16} />
          ) : (
            <NucleoCirclePlay fillColorToken="icon-placeholder" height={16} width={16} />
          )}
        </Stack>
      </Tooltip>
      <Popover open={showPopover} onOpenChange={togglePopover}>
        <Popover.Button className="w-full">
          <Typography
            color={
              isTimerRunningForThisTask || totalTimeSpent > 0
                ? 'text-text-primary'
                : 'text-text-secondary'
            }
            variant="body-short-02">
            {isTimerRunningForThisTask
              ? formatTime(seconds)
              : totalTimeSpent > 0
              ? formatDuration(totalTimeSpent)
              : 'Add time'}
          </Typography>
        </Popover.Button>
        <Popover.Content position="right">
          <TimerMenu projectId={data.fileId ?? ''} taskId={data.id} onClose={off} />
        </Popover.Content>
      </Popover>
    </Stack>
  );
}

interface TimeTrackedProps {
  rowData: TableRowData;
  disableInteractiveTooltip?: boolean;
}

function TimeTracked({ rowData, disableInteractiveTooltip = false }: TimeTrackedProps) {
  const { [FeatureFlags.EnableTaskTimer]: isTaskTimerEnabled } = useFeatureFlags();

  if (!('data' in rowData) || !('id' in rowData.data) || !isTaskTimerEnabled) {
    return null;
  }

  return (
    <TimeTrackedContent data={rowData.data} disableInteractiveTooltip={disableInteractiveTooltip} />
  );
}

export default TimeTracked;
